import { notification } from "antd";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Measure from "react-measure";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Redirect } from "react-router-dom";
import { RailInspectionLoader } from "../datastores/railInspectionStore";
import {
    currentPlaylistPosition,
    fetchUserAnnotationTypes,
    getInspectionBookmarks,
    getRailInspectionConditions,
    getRouteCoordinateSystems,
    railInspectionExited,
    railInspectionOpenedAudit,
    refresh,
    routeSelected,
    selectBookmark,
    toggleInspectionAnnotationMode,
} from "../redux/actions/index";
import DetailView from "./display/image/DetailView";
import RailCompareDraggable from "./display/image/InspectionHistory/RailCompareDraggable";
import AssetNavigation from "./display/image/inspectionNavigation/AssetNavigation";
import ContentNavigation from "./display/image/inspectionNavigation/ContentNavigation";
import SperrySuspectNavigation from "./display/image/inspectionNavigation/SperrySuspectNavigation";
import InspectionSnapshotModal from "./display/image/InspectionSnapshotModal";
import RailInspectBigScroller from "./display/image/RailInspectBigScroller";
import RailInspectControls from "./display/image/RailInspectControls";
import RailInspectHeader from "./display/image/RailInspectHeader";
import RailInspectionExtraDisplay from "./display/image/RailInspectionExtraDisplay";
import RailInspectNormalSource from "./display/image/RailInspectNormalSource";
import Settings from "./display/image/Settings";
import PinCursor from "./DropPinCursor";
import GenericDialog from "./GenericDialog";
import { FilteredDetectionsProvider } from "./hooks/contexts/FilteredDetections";
import useOpenDialogFromURLParams from "./util/useOpenDialogFromURLParams";

const naiveIndexSelector = (state) => state.playlist.position.currentIndex;
const accessTokensSelector = (state) => state.access_token;
const coordsSelector = (state) => state.playlist.position.coords;
const supportedCoordinateSystemsSelector = (state) => state.routeCoordinateSystems;
const reduxSessionIDSelector = (state) => state.playlist.data.routeID;
const inspectionBookmarksSelector = (state) => state.railInspection.bookmarks;
const detailViewOpenSelector = (state) => state.railInspection.detailViewOpen;
const settingsOpenSelector = (state) => state.railInspection.settingsOpen;
const playlistSelector = (state) => state.playlist.data;
const openRailInspectionSourcesSelector = (state) => state.railInspection.railInspectionSourcesOpen;
const railDataObjectSelector = (state) => state.railInspection.railInspectionImages.data;
const detectionsWindowOpenSelector = (state) => state.railInspection.detections.windowOpen;
const sperryWindowOpenSelector = (state) => state.railInspection.suspectViewerOpen;
const annotateModeEnabledSelector = (state) => state.railInspection.annotations.modeEnabled;
const userEmailSelector = (state) => state.userDetails.email;
const displayExtraLayersSelector = (state) => state.railInspection.displayExtraLayers;
const selectedRailInspectionImageTimestampSelector = (state) => state.railInspection.selectedRailInspectionImage.timestamp;

const INITIAL_SECONDARY_RAIL_CONFIG = {
    detail_cameras: [],
    inspection_images: [],
    video_offsets: {},
};

const INITIAL_SECONDARY_RAIL_IMAGE_STATE = {
    loaded: false,
    percentageComplete: 0,
    sessionID: null,
};

const RailInspectionComponent = ({ match }) => {
    const [bookmarksAndAnnotationsWindowOpen, setBookmarksAndAnnotationsWindowOpen] = useState(false);

    const [windowDimensions, setWindowDimensions] = useState({ width: 0, height: 0 });
    const [markerToolActive, setMarkerToolActive] = useState(false);

    const [labelWindowOpen, setLabelWindowOpen] = useState(false);
    const [updatingID, setUpdatingID] = useState();
    const [newRailIndex, setNewRailIndex] = useState();
    const [findModalVisible, setFindModalVisible] = useState(false);
    const [archiveInspectionSessionModalVisible, setArchiveInspection] = useState(false);
    const [restoreArchivedInspectionSessionModalVisible, setRestoreArchivedInspection] = useState(false);
    const [annotationWindowOpen, setAnnotationWindowOpen] = useState(false);
    const [exportWindowOpen, setExportWindowOpen] = useState(false);
    const [fetchedInitialData, setFetchedInitialData] = useState(false);
    const [favouritePopoverOpen, setFavouritePopoverOpen] = useState(false);

    const [secondaryAlignmentAdjustment, setSecondaryAlignmentAdjustment] = useState(null);

    const detectionsOpen = useSelector(detectionsWindowOpenSelector);
    const sperryWindowOpen = useSelector(sperryWindowOpenSelector);
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const naiveIndex = useSelector(naiveIndexSelector);
    const sessionID = parseInt(match.params.sessionID);
    const reduxSessionID = useSelector(reduxSessionIDSelector);
    const accessToken = useSelector(accessTokensSelector);
    const coords = useSelector(coordsSelector);
    const supportedCoordinateSystems = useSelector(supportedCoordinateSystemsSelector);
    const inspectionBookmarks = useSelector(inspectionBookmarksSelector);
    const detailViewOpen = useSelector(detailViewOpenSelector);
    const settingsOpen = useSelector(settingsOpenSelector);
    const playlist = useSelector(playlistSelector);
    const railData = useSelector(railDataObjectSelector);
    const openRailInspectionSources = useSelector(openRailInspectionSourcesSelector);
    const annotateModeEnabled = useSelector(annotateModeEnabledSelector);
    const userEmail = useSelector(userEmailSelector);
    const displayExtraLayers = useSelector(displayExtraLayersSelector);
    const [currentHeaderHeight, setCurrentHeaderHeight] = useState(0);
    const selectedRailInspectionImageTimestamp = useSelector(selectedRailInspectionImageTimestampSelector);

    const [secondaryManualAdjustmentValues, setSecondaryManualAdjustmentValues] = useState(null);
    const [railManualAdjustmentLoading, setRailManualAdjustmentLoading] = useState(false);
    const [extraLayersSyncOffset, setExtraLayersSyncOffset] = useState(0);
    const [reportProblemVisible, setReportProblemVisible] = useState();

    const labelsRef = useRef({});

    const showReportProblemDialog = () => {
        setReportProblemVisible(true);
    };

    const hideReportProblemDialog = () => {
        setReportProblemVisible(false);
    };

    const secondaryRailImageLoader = useRef();

    const [secondaryRailConfig, setSecondaryRailConfig] = useState(INITIAL_SECONDARY_RAIL_CONFIG);
    const [secondaryRailImageStatus, setSecondaryRailImageStatus] = useState(INITIAL_SECONDARY_RAIL_IMAGE_STATE);
    const [secondaryRailImages, setSecondaryRailImages] = useState(null);

    const requestedTimestampUrl = match ? parseInt(match.params.ts) : 0;

    useOpenDialogFromURLParams();

    useEffect(() => {
        const railInspectionLoaderNotify = () => {
            setSecondaryRailConfig(secondaryRailImageLoader.current.config);
            if (secondaryRailImageLoader.current.loaded) {
                setSecondaryRailImages({
                    data: secondaryRailImageLoader.current.data,
                    imageLocations: secondaryRailImageLoader.current.imageLocations,
                });
            } else {
                setSecondaryRailImages(null);
            }
            setSecondaryRailImageStatus({
                loaded: secondaryRailImageLoader.current.loaded && !_.isEmpty(secondaryRailImageLoader.current.imageLocations),
                percentageComplete: secondaryRailImageLoader.current.percentageComplete,
                sessionID: secondaryRailImageLoader.current.sessionID,
            });
        };
        secondaryRailImageLoader.current = new RailInspectionLoader(railInspectionLoaderNotify);
        return () => {
            secondaryRailImageLoader.current.abort();
        };
    }, [setSecondaryRailConfig, setSecondaryRailImages, secondaryRailImageLoader]);

    const loadSecondarySession = useCallback(
        (sessionID) => {
            if (!secondaryRailImageLoader.current) return;
            dispatch(secondaryRailImageLoader.current.load(sessionID));
            dispatch(secondaryRailImageLoader.current.getSessionLocations());
        },
        [dispatch, secondaryRailImageLoader],
    );

    const clearSecondarySession = (error = false) => {
        if (secondaryRailImageLoader.current) {
            secondaryRailImageLoader.current.clear();
            setSecondaryRailImages(null);
            setSecondaryRailConfig(INITIAL_SECONDARY_RAIL_CONFIG);
            setSecondaryRailImageStatus(INITIAL_SECONDARY_RAIL_IMAGE_STATE);
            if (error) {
                notification.warning({
                    message: "Awaiting Inspection Image Upload",
                    description: "The chosen session does not yet have images uploaded, please try a different session.",
                });
            }
        }
    };

    const videolessSession = useMemo(() => {
        return !!(playlist.video?.length === 0 && railData.length);
    }, [playlist, railData]);

    const getInitialData = useCallback(() => {
        if (_.isEmpty(supportedCoordinateSystems)) {
            dispatch(getRouteCoordinateSystems());
        }
        if (naiveIndex === 0 && supportedCoordinateSystems) {
            dispatch(currentPlaylistPosition(0, coords, 0));
        }
        if (!inspectionBookmarks || !inspectionBookmarks.length) {
            dispatch(getInspectionBookmarks(sessionID));
        }

        dispatch(fetchUserAnnotationTypes());
        dispatch(getRailInspectionConditions());
        dispatch(railInspectionOpenedAudit(sessionID));
        setFetchedInitialData(true);
    }, [coords, dispatch, inspectionBookmarks, naiveIndex, sessionID, supportedCoordinateSystems]);

    useEffect(() => {
        if (userEmail && !fetchedInitialData) {
            getInitialData();
        }
    }, [userEmail, getInitialData, fetchedInitialData]);

    useEffect(() => {
        if (naiveIndex === 0 && supportedCoordinateSystems) {
            dispatch(currentPlaylistPosition(0, coords, 0));
        }
    }, [supportedCoordinateSystems]);

    const sessionSelected = useMemo(() => {
        return sessionID === reduxSessionID;
    }, [sessionID, reduxSessionID]);

    const onExitRailInspection = () => {
        dispatch(railInspectionExited());
        dispatch(refresh());
        dispatch(routeSelected(sessionID, selectedRailInspectionImageTimestamp / 1000));
    };

    useEffect(() => {
        if (!sessionSelected) {
            let timestampToRequest = requestedTimestampUrl;
            if (timestampToRequest > 9999999999) {
                timestampToRequest /= 1000;
            }
            dispatch(routeSelected(sessionID, timestampToRequest));
        } else if (selectedRailInspectionImageTimestamp && selectedRailInspectionImageTimestamp !== requestedTimestampUrl) {
            // Persist search params if the URL changes in this useEffect
            const searchParams = location.search;
            const newUrlPath = `/rail-inspection/${sessionID}/${selectedRailInspectionImageTimestamp}${searchParams}`;
            history.replace(newUrlPath);
        }
    }, [dispatch, sessionID, sessionSelected, requestedTimestampUrl, history, selectedRailInspectionImageTimestamp, accessToken]);

    const imageIndexChanged = useCallback(
        (newIndex) => {
            dispatch(selectBookmark(null));
            setNewRailIndex(newIndex);
        },
        [dispatch, setNewRailIndex],
    );

    const toggleMarkerTool = useCallback(() => {
        setMarkerToolActive(!markerToolActive);
        dispatch(toggleInspectionAnnotationMode(false));
    }, [setMarkerToolActive, markerToolActive, dispatch]);

    const toggleFindModalVisibility = useCallback(
        (value) => {
            setFindModalVisible(value);
        },
        [setFindModalVisible],
    );

    const toggleArchiveInspectionSessionModal = useCallback(() => {
        setArchiveInspection(!archiveInspectionSessionModalVisible);
    }, [setArchiveInspection, archiveInspectionSessionModalVisible]);

    const toggleRestoreArchivedInspectionSessionModal = useCallback(() => {
        setRestoreArchivedInspection(!restoreArchivedInspectionSessionModalVisible);
    }, [setRestoreArchivedInspection, restoreArchivedInspectionSessionModalVisible]);

    useEffect(() => {
        if (annotateModeEnabled) {
            setMarkerToolActive(false);
        }
    }, [annotateModeEnabled]);

    const videoWindows = useMemo(() => {
        let filteredSources = [];
        let videos = [];
        if (!videolessSession && playlist && playlist.video && playlist.video.length && openRailInspectionSources) {
            filteredSources = openRailInspectionSources.filter((el) => {
                return playlist.video.indexOf(el.id) < 0;
            });
        }

        if (filteredSources.length) {
            videos = filteredSources.map((item) => {
                let id;
                let isMainSource = false;
                if (item.id === 0) {
                    id = "master";
                    isMainSource = true;
                } else {
                    id = "slave" + item.id;
                }
                return (
                    <RailInspectNormalSource
                        key={id}
                        idx={item.id}
                        match={match}
                        windowDimensions={windowDimensions}
                        isSlave
                        fullscreenID={id}
                        isMainSource={isMainSource}
                    />
                );
            });
        }

        return videos;
    }, [openRailInspectionSources]);

    // don't think below do anything
    if (!accessToken) {
        return <Redirect to={{ pathname: "/login" }} />;
    }

    return (
        <>
            {reportProblemVisible && (
                <GenericDialog
                    title="Report problem"
                    reportOrigin="rail-inspection"
                    promptText="Use this form to report an issue with this inspection"
                    placeholder="Please describe the problem you are having"
                    onClose={hideReportProblemDialog}
                />
            )}
            <Measure
                bounds
                onResize={(contentRect) => {
                    setWindowDimensions({ width: contentRect.bounds.width, height: contentRect.bounds.height, bottom: contentRect.bounds.bottom });
                }}>
                {({ measureRef }) => (
                    <div
                        ref={measureRef}
                        style={{ height: "100%" }}>
                        <PinCursor active={markerToolActive} />
                        {videoWindows}
                        {detailViewOpen && <DetailView windowDimensions={windowDimensions} />}
                        {settingsOpen && (
                            <Settings
                                windowDimensions={windowDimensions}
                                showGridSettings={true}
                                secondaryRailConfig={secondaryRailConfig}
                            />
                        )}
                        <RailCompareDraggable
                            windowDimensions={windowDimensions}
                            loadSecondarySession={loadSecondarySession}
                            clearSecondarySession={clearSecondarySession}
                        />

                        {bookmarksAndAnnotationsWindowOpen && (
                            <ContentNavigation
                                setBookmarksAndAnnotationsWindowOpen={setBookmarksAndAnnotationsWindowOpen}
                                windowDimensions={windowDimensions}
                                annotationWindowOpen={annotationWindowOpen}
                                setLabelWindowOpen={setLabelWindowOpen}
                                labelWindowOpen={labelWindowOpen}
                                setUpdatingID={setUpdatingID}
                                exportWindowOpen={exportWindowOpen}
                                setExportWindowOpen={setExportWindowOpen}
                            />
                        )}
                        {sperryWindowOpen && (
                            <SperrySuspectNavigation
                                windowDimensions={windowDimensions}
                                annotationWindowOpen={annotationWindowOpen}
                                setLabelWindowOpen={setLabelWindowOpen}
                                labelWindowOpen={labelWindowOpen}
                                setUpdatingID={setUpdatingID}
                            />
                        )}
                        {displayExtraLayers && (
                            <RailInspectionExtraDisplay
                                syncOffset={extraLayersSyncOffset}
                                setSyncOffset={setExtraLayersSyncOffset}
                                labelWindowOpen={labelWindowOpen}
                                windowDimensions={windowDimensions}
                                annotationWindowOpen={annotationWindowOpen}
                            />
                        )}
                        <InspectionSnapshotModal patrolView={false} />
                        <div className="RailInspectionMainWrapper">
                            <RailInspectHeader
                                match={match}
                                videoless={videolessSession}
                                toggleFindModalVisibility={toggleFindModalVisibility}
                                findModalVisible={findModalVisible}
                                setFavouritePopoverOpen={setFavouritePopoverOpen}
                                toggleArchiveInspectionSessionModal={toggleArchiveInspectionSessionModal}
                                archiveInspectionSessionModalVisible={archiveInspectionSessionModalVisible}
                                toggleRestoreArchivedInspectionSessionModal={toggleRestoreArchivedInspectionSessionModal}
                                restoreArchivedInspectionSessionModalVisible={restoreArchivedInspectionSessionModalVisible}
                                setCurrentHeaderHeight={setCurrentHeaderHeight}
                                onExitRailInspection={onExitRailInspection}
                                labelsRef={labelsRef}
                            />
                            <FilteredDetectionsProvider>
                                {detectionsOpen && (
                                    <AssetNavigation
                                        windowDimensions={windowDimensions}
                                        annotationWindowOpen={annotationWindowOpen}
                                        setLabelWindowOpen={setLabelWindowOpen}
                                        labelWindowOpen={labelWindowOpen}
                                        setUpdatingID={setUpdatingID}
                                        reportProblemVisible={reportProblemVisible}
                                    />
                                )}
                                <RailInspectBigScroller
                                    match={match}
                                    markerToolActive={markerToolActive}
                                    toggleMarkerTool={toggleMarkerTool}
                                    labelWindowOpen={labelWindowOpen}
                                    setLabelWindowOpen={setLabelWindowOpen}
                                    updatingID={updatingID}
                                    setUpdatingID={setUpdatingID}
                                    pinCursorActive={markerToolActive}
                                    videoless={videolessSession}
                                    newRailIndex={newRailIndex}
                                    findModalVisible={findModalVisible}
                                    annotationWindowOpen={annotationWindowOpen}
                                    setAnnotationWindowOpen={setAnnotationWindowOpen}
                                    favouritePopoverOpen={favouritePopoverOpen}
                                    secondaryRailImages={secondaryRailImages}
                                    secondaryRailConfig={secondaryRailConfig}
                                    setSecondaryManualAdjustmentValues={setSecondaryManualAdjustmentValues}
                                    secondaryManualAdjustmentValues={secondaryManualAdjustmentValues}
                                    setSecondaryAlignmentAdjustment={setSecondaryAlignmentAdjustment}
                                    secondaryAlignmentAdjustment={secondaryAlignmentAdjustment}
                                    railManualAdjustmentLoading={railManualAdjustmentLoading}
                                    setRailManualAdjustmentLoading={setRailManualAdjustmentLoading}
                                    reportProblemVisible={reportProblemVisible}
                                    currentHeaderHeight={currentHeaderHeight}
                                    toggleFindModalVisibility={toggleFindModalVisibility}
                                    toggleArchiveInspectionSessionModal={toggleArchiveInspectionSessionModal}
                                    toggleRestoreArchivedInspectionSessionModal={toggleRestoreArchivedInspectionSessionModal}
                                    showReportProblemDialog={showReportProblemDialog}
                                    clearSecondarySession={clearSecondarySession}
                                    secondaryRailImageStatus={secondaryRailImageStatus}
                                    loadSecondarySession={loadSecondarySession}
                                    assetNavigationWindowOpen={detectionsOpen}
                                    bookmarksAndAnnotationsWindowOpen={bookmarksAndAnnotationsWindowOpen}
                                    setBookmarksAndAnnotationsWindowOpen={setBookmarksAndAnnotationsWindowOpen}
                                    exportWindowOpen={exportWindowOpen}
                                    labelsRef={labelsRef}
                                />
                            </FilteredDetectionsProvider>
                            <RailInspectControls
                                imageIndexChanged={imageIndexChanged}
                                secondaryRailImages={secondaryRailImages}
                                newRailIndex={newRailIndex}
                                secondaryAlignmentAdjustment={secondaryAlignmentAdjustment}
                                setSecondaryAlignmentAdjustment={setSecondaryAlignmentAdjustment}
                            />
                        </div>
                    </div>
                )}
            </Measure>
        </>
    );
};

export default RailInspectionComponent;
